import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button, Form } from 'react-bootstrap';
import { get, post } from '../../Services/API';
import Error from '../../Components/Error';
import { formatDateTime as format } from '../../Utils/timezone';

function AcknowledgeNotification() {
  const { bookingId, operatorId } = useParams();
  const [notification, setNotification] = useState(null);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [acknowledgeLoading, setAcknowledgeLoading] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [cars, setCars] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [selectedCarId, setSelectedCarId] = useState('');
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignmentSuccess, setAssignmentSuccess] = useState(false);
  const shadowRootRef = useRef(null); // Ref for the container where the shadow DOM will be attached
  const acknowledgeRequestedRef = useRef(false);

  const acknowledgeNotification = async () => {
    // The interaction listener and the fallback button can both fire for a
    // single click, so let the first one through and drop the rest. Reset on
    // failure so the button can still retry.
    if (acknowledgeRequestedRef.current) {
      return;
    }

    acknowledgeRequestedRef.current = true;
    setAcknowledgeLoading(true);
    try {
      await post('operators/notifications', { bookingId, operatorId });
      setAcknowledged(true);
      setError(null);
    } catch (err) {
      acknowledgeRequestedRef.current = false;
      setError('Something went wrong, try again');
    } finally {
      setAcknowledgeLoading(false);
    }
  };

  const fetchDriversAndCars = async () => {
    try {
      const [driversResponse, carsResponse] = await Promise.all([
        get('operators/drivers-public', { operatorId }),
        get('operators/cars-public', { operatorId }),
      ]);
      setDrivers(driversResponse.data?.data || []);
      setCars(carsResponse.data?.data || []);
    } catch (err) {
      setError('Failed to load drivers and vehicles');
    }
  };

  const handleAssignDriverAndCar = async () => {
    if (!selectedDriverId && !selectedCarId) {
      setError('Please select at least a driver or a vehicle');
      return;
    }

    setAssignmentLoading(true);
    try {
      await post('bookings/assign-driver-and-car-public', {
        _id: bookingId,
        operatorId,
        operator_driver_id: selectedDriverId || '',
        operator_car_id: selectedCarId || '',
      });

      // Update local booking state to reflect the assignment
      setBooking({
        operator_driver_id: selectedDriverId || '',
        operator_car_id: selectedCarId || '',
      });

      setAssignmentSuccess(true);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to assign driver/vehicle');
    } finally {
      setAssignmentLoading(false);
    }
  };

  useEffect(() => {
    const loadNotification = async () => {
      try {
        const response = await get('operators/notifications', { bookingId, operatorId });
        if (
          response.data?.data
        && Array.isArray(response.data.data)
        && response.data.data.length === 1
        ) {
          const notificationData = response.data.data[0];
          setNotification(notificationData);
          setAcknowledged(!!notificationData.acknowledged);

          // Extract booking data from notification response
          if (notificationData.booking) {
            setBooking(notificationData.booking);
            // Pre-populate selected driver and car if already assigned
            if (notificationData.booking.operator_driver_id) {
              setSelectedDriverId(notificationData.booking.operator_driver_id);
            }
            if (notificationData.booking.operator_car_id) {
              setSelectedCarId(notificationData.booking.operator_car_id);
            }
          }
        } else {
          setError('No notification found');
        }
      } catch (err) {
        setError('Something went wrong, try again');
      }
    };

    loadNotification();
    fetchDriversAndCars();
  }, [bookingId, operatorId]);

  useEffect(() => {
    if (shadowRootRef.current && !shadowRootRef.current.shadowRoot) {
      shadowRootRef.current.attachShadow({ mode: 'open' });
    }

    // Update the content of the shadow DOM when notification changes
    if (notification && shadowRootRef.current?.shadowRoot) {
      shadowRootRef.current.shadowRoot.innerHTML = `<div>${notification.notification}</div>`;
    }
  }, [notification]);

  // Operators reach this page from a link they already clicked in the email,
  // so acknowledging shouldn't cost them a second click. It just must not
  // happen from a bare page load either: link prefetchers, spam filters and
  // URL scanners open this page too, and they were marking jobs acknowledged
  // on the operator's behalf. Waiting for one sign of a real person — a
  // pointer move, scroll, key or touch — costs a human nothing, and an
  // automated fetch that never interacts leaves the job unacknowledged.
  useEffect(() => {
    if (!notification || acknowledged) {
      return undefined;
    }

    const events = ['pointermove', 'pointerdown', 'keydown', 'scroll', 'touchstart'];

    const onInteraction = () => {
      events.forEach((event) => window.removeEventListener(event, onInteraction));
      acknowledgeNotification();
    };

    events.forEach((event) => window.addEventListener(event, onInteraction, { passive: true }));

    return () => events.forEach((event) => window.removeEventListener(event, onInteraction));
  }, [notification, acknowledged]);

  return (
    <>
      <h1>Notification</h1>
      {error && <Error error={error} setError={setError} />}
      {notification && (
        <>
          {acknowledged ? (
            <div className="alert text-bg-success">
              Notification acknowledged, thank you!
            </div>
          ) : (
            <div className="alert alert-warning d-flex justify-content-between align-items-center">
              <span>This job is not acknowledged yet.</span>
              <Button
                variant="success"
                size="sm"
                onClick={acknowledgeNotification}
                disabled={acknowledgeLoading}
              >
                {acknowledgeLoading ? (
                  <>
                    <i className="fa fa-spinner fa-spin me-2" />
                    Acknowledging...
                  </>
                ) : (
                  <>
                    <i className="fa fa-check me-2" />
                    Acknowledge Receipt
                  </>
                )}
              </Button>
            </div>
          )}
          {assignmentSuccess && (
          <div className="alert text-bg-success">
            Driver and vehicle assigned successfully!
          </div>
          )}
          <p>
            Date Sent:
            { ' ' }
            <b className="text-decoration-underline">{format(notification.Date)}</b>
          </p>

          <div className="card mb-3">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Assign Driver & Vehicle</h5>
                <Link to={`/operator-portal/${operatorId}`} className="btn btn-sm btn-outline-primary">
                  <i className="fa fa-cog me-2" />
                  Manage Drivers & Vehicles
                </Link>
              </div>

              {booking && (booking.operator_driver_id || booking.operator_car_id) && (
                <div className="alert alert-info mb-3">
                  <strong>Currently Assigned:</strong>
                  {booking.operator_driver_id && (
                    <div>
                      Driver:
                      {' '}
                      {drivers.find((d) => d.Id === booking.operator_driver_id)?.full_name || 'Unknown'}
                    </div>
                  )}
                  {booking.operator_car_id && (
                    <div>
                      Vehicle:
                      {' '}
                      {cars.find((c) => c.Id === booking.operator_car_id)?.type || 'Unknown'}
                    </div>
                  )}
                </div>
              )}

              <div className="row">
                <Form.Group className="mb-3 col-md-6">
                  <Form.Label>Select Driver</Form.Label>
                  <Form.Select
                    value={selectedDriverId}
                    onChange={(e) => setSelectedDriverId(e.target.value)}
                    size="sm"
                    disabled={assignmentLoading}
                  >
                    <option value="">-- Select a driver --</option>
                    {drivers
                      .filter((driver) => driver.active === 1)
                      .map((driver) => (
                        <option key={driver.Id} value={driver.Id}>
                          {driver.full_name}
                          {driver.phone_number ? ` (${driver.phone_number})` : ''}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3 col-md-6">
                  <Form.Label>Select Vehicle</Form.Label>
                  <Form.Select
                    value={selectedCarId}
                    onChange={(e) => setSelectedCarId(e.target.value)}
                    size="sm"
                    disabled={assignmentLoading}
                  >
                    <option value="">-- Select a vehicle --</option>
                    {cars
                      .filter((car) => car.active === 1)
                      .map((car) => (
                        <option key={car.Id} value={car.Id}>
                          {car.type}
                          {car.color ? ` - ${car.color}` : ''}
                          {car.plate_number ? ` (${car.plate_number})` : ''}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </div>

              <Button
                variant="primary"
                size="sm"
                onClick={handleAssignDriverAndCar}
                disabled={assignmentLoading || (!selectedDriverId && !selectedCarId)}
              >
                {assignmentLoading ? (
                  <>
                    <i className="fa fa-spinner fa-spin me-2" />
                    Assigning...
                  </>
                ) : (
                  <>
                    <i className="fa fa-check me-2" />
                    Assign to Booking
                  </>
                )}
              </Button>
            </div>
          </div>

          <div className="card">
            <div className="card-body" ref={shadowRootRef} />
          </div>
        </>
      )}
    </>
  );
}

export default AcknowledgeNotification;
